import { useState, useEffect, useRef, useMemo } from 'react';
import { writeOrgFile, deleteOrgFile, listOrgCollection } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { callWorker } from '../../utils/workerClient';
import { AUTHORITY_TYPES, AUTHORITY_PURPOSES, AUTHORITY_STATUSES } from '../../utils/enums';
import { COUNTRIES } from '../../utils/countries';
import PeopleSelector from '../shared/PeopleSelector';
import EvidenceSelector from '../shared/EvidenceSelector';
import AISuggestButton from '../shared/AISuggestButton';
import ConfirmDialog from '../shared/ConfirmDialog';
import SaveErrorDialog from '../shared/SaveErrorDialog';
import {
  BG_CONTENT, BG_SURFACE, BG_SUBTLE, SURFACE_WHITE,
  BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE,
  TEXT_MUTED, TEXT_DISABLED, TEXT_HEADING, TEXT_DEFAULT,
  BORDER_DEFAULT, BRAND_PRIMARY_DARK,
  ERROR_TEXT, ERROR_BG, ERROR_BORDER, FIELD_ERROR_BORDER, UNSAVED_AMBER,
  FONT_SECTION_TITLE, FONT_BODY, FONT_BODY_STRONG,
  FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON,
} from '../../theme';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    titleCreate:         'Nueva autoridad',
    titleEdit:           'Editar autoridad',
    s1:                  'Identificación',
    s2:                  'Propósito',
    s3:                  'Contacto externo',
    s4:                  'Gestión interna',
    s5:                  'Estado y evidencia',
    fRef:                'Referencia',
    fName:               'Nombre',
    fShortName:          'Nombre corto',
    fType:               'Tipo',
    fCountry:            'País',
    fJurisdictionScope:  'Ámbito jurisdiccional',
    fJurisdictionHint:   'supranacional o subnacional, opcional',
    fPurpose:            'Propósitos',
    fPurposeNotes:       'Notas sobre el propósito',
    addPurpose:          '+ Añadir propósito',
    selectPurpose:       'Selecciona un propósito…',
    fPrimaryContactName: 'Nombre de contacto principal',
    fPrimaryContactEmail:'Email de contacto principal',
    fPrimaryContactPhone:'Teléfono de contacto principal',
    fWebUrl:             'Sitio web',
    fReportingChannel:   'Canal de notificación',
    fInternalLiaison:    'Enlace interno',
    fReviewFrequency:    'Frecuencia de revisión (meses)',
    fLastContactDate:    'Última fecha de contacto',
    fNextReviewDate:     'Próxima revisión',
    fEvidence:           'Evidencia vinculada',
    fStatus:             'Estado',
    fNotes:              'Notas',
    suggestWithAI:       'Sugerir con IA ✨',
    generateWithAI:      'Generar con IA ✨',
    optional:            '(opcional)',
    btnSave:             'Guardar',
    btnCancel:           'Cancelar',
    btnDelete:           'Eliminar autoridad',
    btnConfirmDelete:    '¿Confirmar eliminación?',
    saving:              'Guardando…',
    saveOk:              '✓ Guardado',
    errorRequired:       'Campo obligatorio',
    errorSave:           'Error al guardar. Inténtalo de nuevo.',
    errorDelete:         'Error al eliminar.',
    required:            '*',
    requiredLegend:      '* Campos obligatorios',
    validationError:     'Por favor completa los campos obligatorios marcados con *.',
    generating:          'Generando…',
    unsavedLabel:        'Cambios sin guardar',
    aiError:             'No se pudo obtener sugerencia. Inténtalo de nuevo.',
    aiErrorOverloaded:   'Servidores ocupados, inténtalo de nuevo en un momento.',
    aiConsentRequired:   'Activa el procesamiento con IA en Configuración para usar esta función.',
    typeLabels: {
      'Data protection authority':          'Autoridad de protección de datos',
      'National CSIRT':                     'CSIRT nacional',
      'Sector regulator':                   'Regulador sectorial',
      'Law enforcement':                    'Fuerzas y cuerpos de seguridad',
      'NIS2 competent authority':           'Autoridad competente NIS2',
      'Critical infrastructure authority':  'Autoridad de infraestructuras críticas',
      'Standardisation body':               'Organismo de normalización',
      'Government cyber agency':            'Agencia gubernamental de ciberseguridad',
      'Other':                              'Otro',
    },
    purposeLabels: {
      'Personal data breach notification':         'Notificación de brechas de datos personales',
      'NIS2 incident notification':                'Notificación de incidentes NIS2',
      'Sector-specific incident reporting':        'Notificación sectorial de incidentes',
      'Cyber threat intelligence sharing':         'Intercambio de inteligencia de amenazas',
      'Vulnerability disclosure / coordination':   'Divulgación / coordinación de vulnerabilidades',
      'Regulatory inquiry / supervision':          'Requerimiento / supervisión regulatoria',
      'Certification or accreditation body':       'Organismo de certificación o acreditación',
      'Law enforcement coordination':              'Coordinación con fuerzas de seguridad',
      'Standards / best-practice consultation':    'Consulta de normas / buenas prácticas',
      'Other':                                     'Otro',
    },
    statusLabels: {
      'Active':   'Activa',
      'Inactive': 'Inactiva',
    },
  },
  en: {
    titleCreate:         'New authority',
    titleEdit:           'Edit authority',
    s1:                  'Identification',
    s2:                  'Purpose',
    s3:                  'External contact',
    s4:                  'Internal management',
    s5:                  'Status and evidence',
    fRef:                'Reference',
    fName:               'Name',
    fShortName:          'Short name',
    fType:               'Type',
    fCountry:            'Country',
    fJurisdictionScope:  'Jurisdiction scope',
    fJurisdictionHint:   'supranational or subnational, optional',
    fPurpose:            'Purposes',
    fPurposeNotes:       'Purpose notes',
    addPurpose:          '+ Add purpose',
    selectPurpose:       'Select a purpose…',
    fPrimaryContactName: 'Primary contact name',
    fPrimaryContactEmail:'Primary contact email',
    fPrimaryContactPhone:'Primary contact phone',
    fWebUrl:             'Website',
    fReportingChannel:   'Reporting channel',
    fInternalLiaison:    'Internal liaison',
    fReviewFrequency:    'Review frequency (months)',
    fLastContactDate:    'Last contact date',
    fNextReviewDate:     'Next review',
    fEvidence:           'Linked evidence',
    fStatus:             'Status',
    fNotes:              'Notes',
    suggestWithAI:       'Suggest with AI ✨',
    generateWithAI:      'Generate with AI ✨',
    optional:            '(optional)',
    btnSave:             'Save',
    btnCancel:           'Cancel',
    btnDelete:           'Delete authority',
    btnConfirmDelete:    'Confirm delete?',
    saving:              'Saving…',
    saveOk:              '✓ Saved',
    errorRequired:       'Required',
    errorSave:           'Save failed. Please try again.',
    errorDelete:         'Delete failed.',
    required:            '*',
    requiredLegend:      '* Required fields',
    validationError:     'Please complete the required fields marked with *.',
    generating:          'Generating…',
    unsavedLabel:        'Unsaved changes',
    aiError:             'Could not get suggestion. Please try again.',
    aiErrorOverloaded:   'Servers are busy, please try again in a moment.',
    aiConsentRequired:   'Enable AI processing in Settings to use this feature.',
    typeLabels: {
      'Data protection authority':          'Data protection authority',
      'National CSIRT':                     'National CSIRT',
      'Sector regulator':                   'Sector regulator',
      'Law enforcement':                    'Law enforcement',
      'NIS2 competent authority':           'NIS2 competent authority',
      'Critical infrastructure authority':  'Critical infrastructure authority',
      'Standardisation body':               'Standardisation body',
      'Government cyber agency':            'Government cyber agency',
      'Other':                              'Other',
    },
    purposeLabels: {
      'Personal data breach notification':         'Personal data breach notification',
      'NIS2 incident notification':                'NIS2 incident notification',
      'Sector-specific incident reporting':        'Sector-specific incident reporting',
      'Cyber threat intelligence sharing':         'Cyber threat intelligence sharing',
      'Vulnerability disclosure / coordination':   'Vulnerability disclosure / coordination',
      'Regulatory inquiry / supervision':          'Regulatory inquiry / supervision',
      'Certification or accreditation body':       'Certification or accreditation body',
      'Law enforcement coordination':              'Law enforcement coordination',
      'Standards / best-practice consultation':    'Standards / best-practice consultation',
      'Other':                                     'Other',
    },
    statusLabels: {
      'Active':   'Active',
      'Inactive': 'Inactive',
    },
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function yyyymmdd() { return new Date().toISOString().slice(0, 10).replace(/-/g, ''); }

function calcNextReviewDate(lastContactDate, reviewFrequencyMonths) {
  if (!reviewFrequencyMonths) return '';
  const months = Number(reviewFrequencyMonths);
  if (!months) return '';
  const base = lastContactDate ? new Date(lastContactDate) : new Date();
  const d = new Date(base);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

export async function generateAuthorityIds(orgId, refPrefix) {
  let authorities = [];
  try { authorities = await listOrgCollection(orgId, 'authorities'); } catch { /* empty */ }

  const day       = yyyymmdd();
  const dayPrefix = `aut-${day}-`;
  let maxFileSeq  = 0;
  authorities.forEach((a) => {
    if (a.id && a.id.startsWith(dayPrefix)) {
      const n = parseInt(a.id.slice(dayPrefix.length), 10);
      if (!isNaN(n) && n > maxFileSeq) maxFileSeq = n;
    }
  });
  const fileId = `${dayPrefix}${String(maxFileSeq + 1).padStart(3, '0')}`;

  const prefix = `${refPrefix}-`;
  let maxRefSeq = 0;
  authorities.forEach((a) => {
    if (a.ref && a.ref.startsWith(prefix)) {
      const n = parseInt(a.ref.slice(prefix.length), 10);
      if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
    }
  });
  const ref = `${prefix}${String(maxRefSeq + 1).padStart(3, '0')}`;

  return { fileId, ref };
}

// ── INITIAL STATE ─────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  name:                  '',
  shortName:             '',
  type:                  '',
  country:               '',
  jurisdictionScope:     '',
  purpose:               [],
  purposeNotes:          '',
  primaryContactName:    '',
  primaryContactEmail:   '',
  primaryContactPhone:   '',
  webUrl:                '',
  reportingChannel:      '',
  internalLiaison:       '',
  reviewFrequencyMonths: '',
  lastContactDate:       '',
  nextReviewDate:        '',
  evidenceId:            null,
  status:                'Active',
  notes:                 '',
};

function authorityToForm(a) {
  return {
    name:                  a.name                  || '',
    shortName:             a.shortName             || '',
    type:                  AUTHORITY_TYPES.includes(a.type) ? a.type : '',
    country:               a.country               || '',
    jurisdictionScope:     a.jurisdictionScope     || '',
    purpose:               Array.isArray(a.purpose) ? a.purpose.filter((p) => AUTHORITY_PURPOSES.includes(p)) : [],
    purposeNotes:          a.purposeNotes          || '',
    primaryContactName:    a.primaryContactName    || '',
    primaryContactEmail:   a.primaryContactEmail   || '',
    primaryContactPhone:   a.primaryContactPhone   || '',
    webUrl:                a.webUrl                || '',
    reportingChannel:      a.reportingChannel      || '',
    internalLiaison:       a.internalLiaison       || '',
    reviewFrequencyMonths: a.reviewFrequencyMonths != null ? String(a.reviewFrequencyMonths) : '',
    lastContactDate:       a.lastContactDate       || '',
    nextReviewDate:        a.nextReviewDate        || '',
    evidenceId:            a.evidenceId            || null,
    status:                AUTHORITY_STATUSES.includes(a.status) ? a.status : 'Active',
    notes:                 a.notes                 || '',
  };
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const labelSt = {
  ...FONT_SMALL,
  display: 'block', color: TEXT_MUTED,
  letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.3rem',
};
const inputSt = (hasErr) => ({
  ...FONT_BODY,
  width: '100%', padding: '0.55rem 0.7rem',
  border: `1px solid ${hasErr ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6, background: SURFACE_WHITE,
  color: TEXT_HEADING, outline: 'none', boxSizing: 'border-box',
});
const textareaSt = (hasErr) => ({ ...inputSt(hasErr), resize: 'vertical', minHeight: 72 });
const selectSt = { ...inputSt(false), cursor: 'pointer' };
const sectionHeading = {
  ...FONT_BODY_STRONG,
  color: TEXT_HEADING,
  margin: '0 0 1rem', paddingBottom: '0.5rem',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
};
const fieldWrap = { marginBottom: '1rem' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' };

function ErrMsg({ msg }) {
  if (!msg) return null;
  return (
    <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0.2rem 0 0' }}>
      {msg}
    </p>
  );
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────
export default function AuthorityForm({ authority, mode, org, lang, onSave, onCancel, onFormChange, saveRequestTick, dirty }) {
  const t      = T[lang] || T.en;
  const isEdit = !!authority;
  const [currentMode, setCurrentMode] = useState(mode || (isEdit ? 'edit' : 'create'));
  const [savedRecord, setSavedRecord] = useState(isEdit ? authority : null);
  const hasChangedRef = useRef(false);

  const [form, setForm] = useState(() => isEdit ? authorityToForm(authority) : { ...EMPTY_FORM });
  const [fileId, setFileId] = useState(isEdit ? authority.id  : null);
  const [ref,    setRef]    = useState(isEdit ? authority.ref : null);
  const [errors, setErrors] = useState({});
  const [saveState, setSaveState] = useState('idle');
  const [saveErrorOpen, setSaveErrorOpen] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showPurposePicker, setShowPurposePicker] = useState(false);

  // AI: per-form Suggest with AI
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiError,      setAiError]      = useState(null);
  // AI: Generate purpose notes
  const [purposeAiGenerating, setPurposeAiGenerating] = useState(false);
  const [purposeAiError,      setPurposeAiError]      = useState(null);

  const refPrefix = (org.refPrefixes && org.refPrefixes.authority) || 'aut';

  // Generate IDs on create
  useEffect(() => {
    if (isEdit) return;
    generateAuthorityIds(org.id, refPrefix)
      .then(({ fileId: fid, ref: r }) => { setFileId(fid); setRef(r); })
      .catch(() => { setFileId(`aut-${yyyymmdd()}-001`); setRef(`${refPrefix}-001`); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-calculate nextReviewDate when lastContactDate or reviewFrequencyMonths changes
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      nextReviewDate: calcNextReviewDate(prev.lastContactDate, prev.reviewFrequencyMonths),
    }));
  }, [form.lastContactDate, form.reviewFrequencyMonths]);

  const markChange = () => {
    if (!hasChangedRef.current && onFormChange) {
      hasChangedRef.current = true;
      onFormChange();
    }
  };

  const sf  = (field) => (val) => { markChange(); setForm((p) => ({ ...p, [field]: val })); };
  const hic = (field) => (e)   => sf(field)(e.target.value);

  const addPurpose = (value) => {
    if (!AUTHORITY_PURPOSES.includes(value)) return;
    if (form.purpose.includes(value)) return;
    sf('purpose')([...form.purpose, value]);
    setShowPurposePicker(false);
  };

  const removePurpose = (value) => {
    sf('purpose')(form.purpose.filter((p) => p !== value));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t.errorRequired;
    if (!form.type)        errs.type = t.errorRequired;
    if (!form.country)     errs.country = t.errorRequired;
    if (!Array.isArray(form.purpose) || form.purpose.length === 0) errs.purpose = t.errorRequired;
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    if (!fileId || !ref) return;
    setSaveState('saving');
    const now = new Date().toISOString();
    const changedBy = org.consultantPersonId || 'system';
    try {
      const payload = {
        id:                    fileId,
        orgId:                 org.id,
        ref,
        norms:                 Array.isArray(savedRecord?.norms) ? savedRecord.norms : ['iso27001'],
        name:                  form.name.trim(),
        shortName:             form.shortName.trim() || null,
        type:                  form.type,
        country:               form.country,
        jurisdictionScope:     form.jurisdictionScope.trim() || null,
        purpose:               form.purpose,
        purposeNotes:          form.purposeNotes.trim(),
        primaryContactName:    form.primaryContactName.trim() || null,
        primaryContactEmail:   form.primaryContactEmail.trim() || null,
        primaryContactPhone:   form.primaryContactPhone.trim() || null,
        webUrl:                form.webUrl.trim() || null,
        reportingChannel:      form.reportingChannel.trim() || null,
        internalLiaison:       form.internalLiaison || null,
        reviewFrequencyMonths: form.reviewFrequencyMonths === '' ? null : (Number(form.reviewFrequencyMonths) || null),
        lastContactDate:       form.lastContactDate || null,
        nextReviewDate:        form.nextReviewDate || null,
        evidenceId:            form.evidenceId || null,
        status:                form.status,
        notes:                 form.notes.trim(),
        createdAt:             savedRecord?.createdAt || now,
        updatedAt:             now,
      };
      payload.changeLog = savedRecord ? appendChangeLog(savedRecord, payload, changedBy) : [];
      await writeOrgFile(org.id, `authorities/${fileId}.json`, payload);
      setSavedRecord(payload);
      if (currentMode !== 'edit') setCurrentMode('edit');
      onSave(fileId);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('error');
      setSaveErrorOpen(true);
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  const handleDiscardChanges = () => {
    setSaveErrorOpen(false);
    if (savedRecord) {
      setForm(authorityToForm(savedRecord));
      hasChangedRef.current = false;
      setErrors({});
    }
    onCancel();
  };
  const handleRetrySave = () => {
    setSaveErrorOpen(false);
    handleSave();
  };

  useEffect(() => {
    if (saveRequestTick > 0) handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  const handleDelete = async () => {
    try {
      await deleteOrgFile(org.id, `authorities/${fileId}.json`);
      onSave(null);
    } catch {
      setDeleteError(t.errorDelete);
      setConfirmState(null);
    }
  };

  // ── Sanitised AI payloads ─────────────────────────────────────────────────
  const sanitisedAiPayload = useMemo(() => ({
    sector:       org.sector || '',
    size:         org.size || '',
    country:      Array.isArray(org.country) ? org.country.join(',') : (org.country || ''),
    technologies: Array.isArray(org.technologies) ? org.technologies : [],
    regions:      Array.isArray(org.regions) ? org.regions : [],
    norms:        Array.isArray(org.norms) ? org.norms : ['iso27001'],
    type:                form.type || '',
    authorityCountry:    form.country || '',
    jurisdictionScope:   form.jurisdictionScope || '',
    purposeEnumValues:   Array.isArray(form.purpose) ? form.purpose : [],
  }), [org.sector, org.size, org.country, org.technologies, org.norms, form.type, form.country, form.jurisdictionScope, form.purpose, org.regions]);

  // ── AI: Suggest with AI (form header) ─────────────────────────────────────
  const handleSuggestForm = async () => {
    if (aiSuggesting) return;
    if (!org.aiProcessingConsent) return;
    if (!form.type || !form.country) return;
    setAiSuggesting(true);
    setAiError(null);

    const prompt = `You are an expert in information security regulators and supervisory authorities.
Given the organization context and the partially completed Authority record below, infer the most likely authority that matches the type and country, and return its publicly known descriptive fields.

Organization sector: ${sanitisedAiPayload.sector || 'Not specified'}
Organization size: ${sanitisedAiPayload.size || 'Not specified'}
Organization country (ISO codes): ${sanitisedAiPayload.country || 'Not specified'}
Organization technologies: ${(sanitisedAiPayload.technologies || []).join(', ') || 'Not specified'}
Activated norms: ${(sanitisedAiPayload.norms || []).join(', ')}
Authority type: ${sanitisedAiPayload.type}
Authority country (ISO code): ${sanitisedAiPayload.authorityCountry}
Authority jurisdiction scope (free text, may be empty): ${sanitisedAiPayload.jurisdictionScope || 'unspecified'}

Respond ONLY with a valid JSON object, no markdown fences, no preamble. All fields are optional; omit any field you are not confident about. Allowed purpose values are exactly: ${JSON.stringify(AUTHORITY_PURPOSES)}. Do not invent purpose values outside that list.

Return exactly this JSON structure (omit fields when not confident):
{
"name": "string",
"shortName": "string",
"jurisdictionScope": "string",
"webUrl": "string",
"reportingChannel": "string",
"primaryContactName": "string",
"primaryContactPhone": "string",
"primaryContactEmail": "string",
"purposeNotes": "string",
"purpose": ["string", "string"]
}`;

    const result = await callWorker({ prompt, maxTokens: 600 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiError(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggesting(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiError(t.aiError);
      setAiSuggesting(false);
      return;
    }
    const stringFields = ['name', 'shortName', 'jurisdictionScope', 'webUrl', 'reportingChannel', 'primaryContactName', 'primaryContactPhone', 'primaryContactEmail', 'purposeNotes'];
    setForm((prev) => {
      const next = { ...prev };
      let touched = false;
      for (const field of stringFields) {
        if (typeof parsed[field] === 'string' && parsed[field].trim() && !(prev[field] || '').trim()) {
          next[field] = parsed[field].trim();
          touched = true;
        }
      }
      if (Array.isArray(parsed.purpose)) {
        const validNew = parsed.purpose.filter((p) => AUTHORITY_PURPOSES.includes(p) && !prev.purpose.includes(p));
        if (validNew.length > 0) {
          next.purpose = [...prev.purpose, ...validNew];
          touched = true;
        }
      }
      if (touched) markChange();
      return next;
    });
    setAiSuggesting(false);
  };

  // ── AI: Generate purpose notes ────────────────────────────────────────────
  const handleGeneratePurposeNotes = async () => {
    if (purposeAiGenerating) return;
    if (!org.aiProcessingConsent) return;
    if (!form.type || !form.country || form.purpose.length === 0) return;
    setPurposeAiGenerating(true);
    setPurposeAiError(null);

    const prompt = `You are an ISO 27001 Lead Implementer assistant. Draft a short (2-4 sentences) note describing what the organization typically interacts with this authority for, given the organization profile and the selected purpose enum values.

Organization sector: ${sanitisedAiPayload.sector || 'Not specified'}
Organization size: ${sanitisedAiPayload.size || 'Not specified'}
Organization country: ${sanitisedAiPayload.country || 'Not specified'}
Organization technologies: ${(sanitisedAiPayload.technologies || []).join(', ') || 'Not specified'}
Activated norms: ${(sanitisedAiPayload.norms || []).join(', ')}
Authority type: ${sanitisedAiPayload.type}
Authority country: ${sanitisedAiPayload.authorityCountry}
Authority jurisdiction scope: ${sanitisedAiPayload.jurisdictionScope || 'unspecified'}
Selected purposes: ${(sanitisedAiPayload.purposeEnumValues || []).join('; ') || 'none'}

Respond ONLY with a valid JSON object of the form: {"purposeNotes": "string"} — no markdown fences, no preamble.`;

    const result = await callWorker({ prompt, maxTokens: 400 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setPurposeAiError(busy ? t.aiErrorOverloaded : t.aiError);
      setPurposeAiGenerating(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setPurposeAiError(t.aiError);
      setPurposeAiGenerating(false);
      return;
    }
    if (typeof parsed.purposeNotes === 'string' && parsed.purposeNotes.trim() && !form.purposeNotes.trim()) {
      sf('purposeNotes')(parsed.purposeNotes.trim());
    }
    setPurposeAiGenerating(false);
  };

  const disabled = saveState === 'saving';

  const btnPrimary = {
    ...BTN_PRIMARY, ...FONT_BUTTON,
    background: saveState === 'saving' ? TEXT_MUTED : saveState === 'ok' ? BRAND_PRIMARY_DARK : BTN_PRIMARY.background,
    border: 'none', borderRadius: 8,
    padding: '0.55rem 1.25rem', cursor: disabled ? 'default' : 'pointer',
  };
  const btnSecondary = {
    ...BTN_SECONDARY, ...FONT_BUTTON,
    borderRadius: 8,
    padding: '0.55rem 1.25rem', cursor: disabled ? 'default' : 'pointer',
  };

  const purposesRemaining = AUTHORITY_PURPOSES.filter((p) => !form.purpose.includes(p));
  const noConsent = !org.aiProcessingConsent;
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div style={{ background: BG_SURFACE, borderRadius: 12, border: `1px solid ${BORDER_DEFAULT}`, padding: '2rem' }}>
      <ConfirmDialog
        open={confirmState !== null}
        message={t.btnConfirmDelete}
        onConfirm={handleDelete}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel}
      />

      {/* Header */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ ...FONT_SECTION_TITLE, color: TEXT_HEADING, margin: 0 }}>
          {currentMode === 'edit' ? t.titleEdit : t.titleCreate}
          {ref && (
            <span style={{ ...FONT_LABEL, color: TEXT_MUTED, marginLeft: '0.75rem' }}>
              {ref}
            </span>
          )}
          {!ref && currentMode !== 'edit' && (
            <span style={{ ...FONT_SMALL, color: TEXT_DISABLED, marginLeft: '0.5rem' }}>
              {t.generating}
            </span>
          )}
        </h2>
        <AISuggestButton
          onClick={handleSuggestForm}
          loading={aiSuggesting}
          disabled={noConsent || aiSuggesting || !form.type || !form.country}
          lang={lang}
          label={{ es: t.suggestWithAI, en: t.suggestWithAI }}
        />
      </div>
      <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: '0 0 1rem' }}>{t.requiredLegend}</p>
      {aiError && (
        <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 1rem' }}>{aiError}</p>
      )}
      {noConsent && (
        <p style={{ ...FONT_SMALL, color: TEXT_MUTED, margin: '0 0 1rem' }}>{t.aiConsentRequired}</p>
      )}
      {hasErrors && (
        <div style={{
          ...FONT_LABEL,
          background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`,
          borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.25rem',
          color: ERROR_TEXT,
        }}>
          {t.validationError}
        </div>
      )}

      <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {/* ── Section 1: Identification ──────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}><span>{t.s1}</span></h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fRef}</label>
            <input
              value={ref || ''}
              readOnly
              style={{ ...inputSt(false), background: BG_CONTENT, color: TEXT_MUTED, cursor: 'default' }}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fName} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <input value={form.name} onChange={hic('name')} disabled={disabled} style={inputSt(!!errors.name)} />
            <ErrMsg msg={errors.name} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fShortName} <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span></label>
            <input value={form.shortName} onChange={hic('shortName')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fType} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <select value={form.type} onChange={hic('type')} disabled={disabled} style={{ ...selectSt, border: `1px solid ${errors.type ? FIELD_ERROR_BORDER : BORDER_DEFAULT}` }}>
              <option value="">—</option>
              {AUTHORITY_TYPES.map((v) => <option key={v} value={v}>{t.typeLabels[v]}</option>)}
            </select>
            <ErrMsg msg={errors.type} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fCountry} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <select value={form.country} onChange={hic('country')} disabled={disabled} style={{ ...selectSt, border: `1px solid ${errors.country ? FIELD_ERROR_BORDER : BORDER_DEFAULT}` }}>
              <option value="">—</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {lang === 'es' ? c.nameEs : c.nameEn}
                </option>
              ))}
            </select>
            <ErrMsg msg={errors.country} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>
              {t.fJurisdictionScope}{' '}
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span>
            </label>
            <input value={form.jurisdictionScope} onChange={hic('jurisdictionScope')} disabled={disabled} style={inputSt(false)} />
            <p style={{ ...FONT_TINY, color: TEXT_DISABLED, margin: '0.25rem 0 0' }}>{t.fJurisdictionHint}</p>
          </div>
        </div>
      </div>

      {/* ── Section 2: Purpose ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>
          <span>{t.s2}</span>
          <AISuggestButton
            onClick={handleGeneratePurposeNotes}
            loading={purposeAiGenerating}
            disabled={noConsent || purposeAiGenerating || !form.type || !form.country || form.purpose.length === 0}
            lang={lang}
            label={{ es: t.generateWithAI, en: t.generateWithAI }}
          />
        </h3>
        {purposeAiError && (
          <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 0.75rem' }}>{purposeAiError}</p>
        )}
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fPurpose} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
            {form.purpose.map((p) => (
              <span key={p} style={{
                ...FONT_TINY,
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                background: BG_SUBTLE, color: TEXT_DEFAULT, borderRadius: 4,
                padding: '0.2rem 0.4rem 0.2rem 0.55rem',
                border: `1px solid ${BORDER_DEFAULT}`,
              }}>
                {t.purposeLabels[p] || p}
                <button
                  type="button"
                  onClick={() => removePurpose(p)}
                  disabled={disabled}
                  style={{ background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer', color: TEXT_MUTED, padding: 0, fontSize: '0.85rem', lineHeight: 1 }}
                >×</button>
              </span>
            ))}
          </div>
          {showPurposePicker ? (
            <select
              autoFocus
              value=""
              onChange={(e) => addPurpose(e.target.value)}
              onBlur={() => setShowPurposePicker(false)}
              disabled={disabled}
              style={{ ...selectSt, maxWidth: 360 }}
            >
              <option value="">{t.selectPurpose}</option>
              {purposesRemaining.map((p) => (
                <option key={p} value={p}>{t.purposeLabels[p] || p}</option>
              ))}
            </select>
          ) : (
            purposesRemaining.length > 0 && (
              <button
                type="button"
                onClick={() => setShowPurposePicker(true)}
                disabled={disabled}
                style={{ ...BTN_SECONDARY, ...FONT_TINY, borderRadius: 6, padding: '0.3rem 0.7rem', cursor: disabled ? 'default' : 'pointer' }}
              >
                {t.addPurpose}
              </button>
            )
          )}
          <ErrMsg msg={errors.purpose} />
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>
            {t.fPurposeNotes}{' '}
            <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span>
          </label>
          <textarea value={form.purposeNotes} onChange={hic('purposeNotes')} disabled={disabled} style={textareaSt(false)} />
        </div>
      </div>

      {/* ── Section 3: External contact ────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}><span>{t.s3}</span></h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fPrimaryContactName} <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span></label>
            <input value={form.primaryContactName} onChange={hic('primaryContactName')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fPrimaryContactEmail} <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span></label>
            <input type="email" value={form.primaryContactEmail} onChange={hic('primaryContactEmail')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fPrimaryContactPhone} <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span></label>
            <input value={form.primaryContactPhone} onChange={hic('primaryContactPhone')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fWebUrl} <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span></label>
            <input value={form.webUrl} onChange={hic('webUrl')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
            <label style={labelSt}>{t.fReportingChannel} <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span></label>
            <input value={form.reportingChannel} onChange={hic('reportingChannel')} disabled={disabled} style={inputSt(false)} />
          </div>
        </div>
      </div>

      {/* ── Section 4: Internal management ─────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}><span>{t.s4}</span></h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fInternalLiaison} <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span></label>
            <PeopleSelector orgId={org.id} lang={lang} value={form.internalLiaison} onChange={sf('internalLiaison')} disabled={disabled} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fReviewFrequency} <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span></label>
            <input
              type="number" min="0" max="60"
              value={form.reviewFrequencyMonths}
              onChange={(e) => sf('reviewFrequencyMonths')(e.target.value)}
              disabled={disabled}
              style={inputSt(false)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fLastContactDate} <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span></label>
            <input type="date" value={form.lastContactDate} onChange={hic('lastContactDate')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fNextReviewDate}</label>
            <input
              type="date"
              value={form.nextReviewDate}
              readOnly
              style={{ ...inputSt(false), background: BG_CONTENT, color: TEXT_MUTED, cursor: 'default' }}
            />
          </div>
        </div>
      </div>

      {/* ── Section 5: Status and evidence ─────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}><span>{t.s5}</span></h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fStatus}</label>
            <select value={form.status} onChange={hic('status')} disabled={disabled} style={selectSt}>
              {AUTHORITY_STATUSES.map((v) => <option key={v} value={v}>{t.statusLabels[v]}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fEvidence} <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span></label>
            <EvidenceSelector
              orgId={org.id}
              lang={lang}
              value={form.evidenceId}
              onChange={(v) => sf('evidenceId')(v)}
              multiple={false}
              disabled={disabled}
              density="compact"
            />
          </div>
          <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
            <label style={labelSt}>{t.fNotes} <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span></label>
            <textarea value={form.notes} onChange={hic('notes')} disabled={disabled} style={textareaSt(false)} />
          </div>
        </div>
      </div>

      </fieldset>

      {/* ── Sticky action bar ─────────────────────────────────────────────── */}
      <div style={{
        position: 'sticky',
        bottom: 0,
        marginLeft: '-2rem',
        marginRight: '-2rem',
        marginBottom: '-2rem',
        marginTop: '1.5rem',
        padding: '0.85rem 2rem',
        background: BG_SURFACE,
        borderTop: `1px solid ${BORDER_DEFAULT}`,
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        zIndex: 10,
      }}>
        {saveState === 'error' && (
          <div style={{ ...FONT_LABEL, background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`, borderRadius: 6, padding: '0.35rem 0.75rem', marginBottom: '0.5rem' }}>
            {t.errorSave}
          </div>
        )}
        {deleteError && (
          <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>{deleteError}</p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            {currentMode === 'edit' && (
              <button
                onClick={() => setConfirmState({ confirmLabel: t.btnDelete })}
                disabled={disabled}
                style={{ ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, borderRadius: 8, padding: '0.5rem 1rem', cursor: disabled ? 'not-allowed' : 'pointer' }}
              >
                {t.btnDelete}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {dirty && (
              <span style={{ ...FONT_LABEL, color: UNSAVED_AMBER }}>{t.unsavedLabel}</span>
            )}
            <button onClick={onCancel} disabled={disabled} style={btnSecondary}>{t.btnCancel}</button>
            <button onClick={handleSave} disabled={disabled} style={btnPrimary}>{saveState === 'saving' ? t.saving : saveState === 'ok' ? t.saveOk : t.btnSave}</button>
          </div>
        </div>
      </div>

      <SaveErrorDialog
        open={saveErrorOpen}
        lang={lang}
        onRetry={handleRetrySave}
        onDiscard={handleDiscardChanges}
      />
    </div>
  );
}
